import {makeAutoObservable} from "mobx";
import {logEvent} from "analytics";
import {gtmEvent} from "utils/gtm";
import {completeOnboardingRequest} from "requests";
import type {RootStore} from "stores/rootStore";
import type {IUserSettings} from "types";
import {Placement} from 'tippy.js';
import {FEATURES, isFeatureEnabled} from "config";


export type OnboardingStepInfo = {
    /** Fluent translation key for the step title */
    title: string;
    /** Fluent translation key for the step description */
    description: string;
    arrowPlacement: Placement;
    mobile?: Partial<OnboardingStepInfo>;
    beforeCb?: (root: RootStore) => void;
    afterCB?: (root: RootStore) => void;
};

const ONBOARDING_STEPS: Record<string, OnboardingStepInfo> = {
    "scan": {
        title: 'onboarding-scan-title',
        description: 'onboarding-scan-description',
        arrowPlacement: 'top',
    },
    'certificate': {
        title: 'onboarding-certificate-title',
        description: 'onboarding-certificate-description',
        arrowPlacement: 'top',
    },
    'history': {
        title: 'onboarding-history-title',
        description: 'onboarding-history-description',
        arrowPlacement: 'top-start',
        beforeCb: (root) => {
            document.getElementsByClassName("history-header")?.[0]?.scrollIntoView({behavior: "smooth", block: "start"})
        },
        afterCB: (root) => {
            document.getElementsByClassName("scan__header")?.[0]?.scrollIntoView({behavior: "smooth", block: "center"})
        },
        mobile: {
            description: 'onboarding-history-mobile-description',
            arrowPlacement: 'bottom-start',
            beforeCb: (root) => {
                root.layout.setSidebarOpen(true);
            },
            afterCB: (root) => {
                root.layout.setSidebarOpen(false);
            }
        }
    },
    'statistics': {
        title: 'onboarding-statistics-title',
        description: 'onboarding-statistics-description',
        arrowPlacement: 'right-start',
    },
    'api': {
        title: 'onboarding-api-title',
        description: 'onboarding-api-description',
        arrowPlacement: 'right-start',
    },
    "scan-options": {
        title: 'onboarding-scan-options-title',
        description: 'onboarding-scan-options-description',
        arrowPlacement: 'top-start',
        beforeCb: (root) => {
            root.layout.setScanOptionsOpen(true);
        },
        afterCB: (root) => {
            root.layout.setScanOptionsOpen(false);
        }
    }
};

const DESKTOP_ONBOARDING_STEPS = [
    'scan',
    'certificate',
    'history',
    'statistics',
    'api',
];
const MOBILE_ONBOARDING_STEPS = [
    'scan',
    "scan-options",
    'certificate',
    'history',
];

export class OnboardingStore {
    root: RootStore;
    currentStepNo: number = 0;

    constructor(root: RootStore) {
        this.root = root;
        makeAutoObservable(this);
    }

    get currentStep(): string | undefined {
        return this.getStepByNo(this.currentStepNo);
    }

    getStepInfo(step?: string): OnboardingStepInfo | undefined {
        if (!step) return;
        let info = ONBOARDING_STEPS[step];
        if (!info) return;
        const isMobile = this.root.layout.isMobile;
        if (isMobile) {
            info = {
                ...info,
                ...info.mobile,
            }
        }
        return info;
    }

    getSteps(): string[] {
        const isMobile = this.root.layout.isMobile;
        return isMobile ? MOBILE_ONBOARDING_STEPS : DESKTOP_ONBOARDING_STEPS;
    }

    getStepByNo(stepNo: number): string | undefined {
        if (!stepNo) return;
        if (stepNo > this.getTotalSteps()) return;
        return this.getSteps()[stepNo - 1]
    }

    getCurrentStepInfo(): OnboardingStepInfo | undefined {
        if (!this.currentStep) return;
        return this.getStepInfo(this.currentStep);
    }

    getTotalSteps(): number {
        return this.getSteps().length;
    }

    async incrementStep() {
        if (!this.currentStepNo) return;

        logEvent(gtmEvent('GTM_onboarding_step_viewed', {step: this.currentStepNo, page_type: 'app_scan', entrypoint: 'onboarding'}));

        if (this.currentStepNo === this.getTotalSteps()) {
            await this.stopOnboarding();
        } else {
            this.setStep(this.currentStepNo + 1);
        }
    }

    startOnboarding(force: boolean = false) {
        // Auto-start is gated behind a feature flag (off by default). The manual
        // `?onboarding=true` force (init.js) always bypasses the flag for debugging —
        // nothing external links to it, so it can only be set by hand.
        if (!force && !isFeatureEnabled(FEATURES.ONBOARDING)) return;
        const user: IUserSettings | null = this.root.getUser() as any;
        if ((!user || user?.onboarding_completed) && !force) {
            console.log("Skip onboarding", user?.onboarding_completed, user, )
          return;
        }
        if (typeof document !== 'undefined') {
            const tippyArrows = document.querySelectorAll('.tippy-arrow');
            setInterval(() => {
                tippyArrows.forEach((el) => {
                    el.className = el.className + ' onboarding-arrow';
                });
            }, 1000);
        }
        this.setStep(1);
    }

    completeOnboarding = async () => {
        try {
            await completeOnboardingRequest();
            await this.root.user.refresh();
        } catch (err) {
            this.root.toast.error('onboarding-complete-error', err);
        }
    };

    setStep(stepNo: number) {
        if (this.currentStepNo) {
            this.getStepInfo(this.getStepByNo(this.currentStepNo))?.afterCB?.(this.root);
        }
        if (!stepNo) {
            this.currentStepNo = stepNo;
            return
        }
        this.getStepInfo(this.getStepByNo(stepNo))?.beforeCb?.(this.root);
        this.currentStepNo = stepNo;
    }

    async stopOnboarding() {
        this.setStep(0);
        await this.completeOnboarding();
    }
}
